import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { adminSwal as Swal } from "./adminSwal";
import { adminTurmasApi } from "../../../api/adminTurmas";
import { adminApi } from "../../../api/admin";
import { adminCoordenacoesApi } from "../../../api/adminCoordenacoes";
import { TipoUsuario } from "../../../api/usuarios";
import { formatContatoDisplay } from "../../../utils/formatting";
import "./AdminInstituicaoTurmas.scss";

// Escapa texto para uso seguro em innerHTML dos modais SweetAlert.
const escHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
const escAttr = (s) => escHtml(s).replace(/"/g, "&quot;");

// Estilos das listas de checkbox dos modais (injetado no html do SweetAlert).
const CHECKLIST_STYLES = `
<style>
  .ait-form { text-align: left; }
  .ait-input {
    width: 100%; box-sizing: border-box; margin: 0 0 .6rem;
    padding: .6rem .75rem; border: 1px solid #cbd5e1; border-radius: 8px;
    font-size: .95rem; background: #fff;
  }
  .ait-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.15); }
  .ait-field { margin-top: .9rem; }
  .ait-field__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: .35rem; }
  .ait-field__label { font-size: .9rem; font-weight: 700; color: #1e293b; }
  .ait-count {
    font-size: .72rem; font-weight: 600; color: #3b82f6;
    background: #eff6ff; border-radius: 999px; padding: .15rem .55rem;
  }
  .ait-search {
    width: 100%; box-sizing: border-box; margin-bottom: .4rem;
    padding: .5rem .7rem; border: 1px solid #e2e8f0; border-radius: 8px; font-size: .85rem;
  }
  .ait-search:focus { outline: none; border-color: #3b82f6; }
  .ait-list {
    max-height: 190px; overflow-y: auto; border: 1px solid #e2e8f0;
    border-radius: 10px; background: #f8fafc; padding: .25rem;
  }
  .ait-row {
    display: flex; align-items: center; gap: .6rem; padding: .5rem .6rem;
    border-radius: 8px; cursor: pointer; transition: background .12s;
  }
  .ait-row:hover { background: #eef2f7; }
  .ait-row__main { font-size: .9rem; color: #1e293b; font-weight: 600; }
  .ait-row__sub { font-size: .76rem; color: #64748b; margin-left: auto; }
  .ait-cb { width: 17px; height: 17px; accent-color: #3b82f6; cursor: pointer; flex: 0 0 auto; }
  .ait-row:has(.ait-cb:checked) { background: #e0edff; }
  .ait-row:has(.ait-cb:checked) .ait-row__main { color: #1d4ed8; }
</style>`;

export default function AdminInstituicaoTurmas() {
  const { id: instId } = useParams();
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [turmaAberta, setTurmaAberta] = useState(null);
  const [detalhes, setDetalhes] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setTurmas(await adminTurmasApi.listar(instId));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao listar turmas", "error");
    } finally {
      setLoading(false);
    }
  }, [instId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const carregarDetalhes = async (turmaId) => {
    try {
      const detalhe = await adminTurmasApi.obter(instId, turmaId);
      setDetalhes((prev) => ({ ...prev, [turmaId]: detalhe }));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao carregar detalhes", "error");
    }
  };

  const onCriar = async () => {
    let coords = [];
    let alunos = [];
    try {
      coords = (await adminCoordenacoesApi.listar(instId)) || [];
    } catch {
      coords = [];
    }
    try {
      const res = await adminApi.listarUsuarios({
        instituicaoId: instId,
        tipo: TipoUsuario.Aluno,
        pageSize: 500,
      });
      alunos = res.items || [];
    } catch {
      alunos = [];
    }

    if (coords.length === 0) {
      Swal.fire(
        "Sem coordenações",
        "Cadastre ao menos uma coordenação nesta instituição antes de criar turmas.",
        "warning",
      );
      return;
    }
    if (alunos.length === 0) {
      Swal.fire(
        "Sem alunos",
        "Cadastre ao menos um aluno nesta instituição antes de criar turmas.",
        "warning",
      );
      return;
    }

    const coordRows = coords
      .map(
        (c) => `
        <label class="ait-row" data-busca="${escAttr(c.nome.toLowerCase())}">
          <input type="checkbox" class="ait-cb ait-cb-coord" value="${escAttr(c.id)}" />
          <span class="ait-row__main">${escHtml(c.nome)}</span>
        </label>`,
      )
      .join("");
    const alunoRows = alunos
      .map(
        (u) => `
        <label class="ait-row" data-busca="${escAttr(
          `${u.nome} ${u.email || ""} ${u.user || ""}`.toLowerCase(),
        )}">
          <input type="checkbox" class="ait-cb ait-cb-aluno" value="${escAttr(u.id)}" />
          <span class="ait-row__main">${escHtml(u.nome)}</span>
          <span class="ait-row__sub">${escHtml(u.email || u.user || "")}</span>
        </label>`,
      )
      .join("");

    const { value, isConfirmed } = await Swal.fire({
      title: "Nova turma",
      width: 600,
      html: `
        ${CHECKLIST_STYLES}
        <div class="ait-form">
          <input id="sw-nome" class="ait-input" placeholder="Nome da turma" />
          <input id="sw-comp" class="ait-input" placeholder="Complemento (opcional)" />
          <select id="sw-tipo" class="ait-input">
            <option value="primario">Primária</option>
            <option value="secundario">Secundária</option>
          </select>

          <div class="ait-field">
            <div class="ait-field__head">
              <span class="ait-field__label">Coordenações</span>
              <span class="ait-count" id="sw-count-coord">0 selecionada(s)</span>
            </div>
            <div class="ait-list" id="sw-coord-list">${coordRows}</div>
          </div>

          <div class="ait-field">
            <div class="ait-field__head">
              <span class="ait-field__label">Alunos</span>
              <span class="ait-count" id="sw-count-aluno">0 selecionado(s)</span>
            </div>
            <input id="sw-aluno-busca" class="ait-search" placeholder="Buscar aluno por nome, e-mail ou matrícula..." />
            <div class="ait-list" id="sw-aluno-list">${alunoRows}</div>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Criar",
      cancelButtonText: "Cancelar",
      didOpen: () => {
        const upd = (sel, elId, palavra) => {
          const n = document.querySelectorAll(sel).length;
          document.getElementById(elId).textContent = `${n} ${palavra}`;
        };
        const refresh = () => {
          upd(".ait-cb-coord:checked", "sw-count-coord", "selecionada(s)");
          upd(".ait-cb-aluno:checked", "sw-count-aluno", "selecionado(s)");
        };
        document
          .getElementById("sw-coord-list")
          .addEventListener("change", refresh);
        document
          .getElementById("sw-aluno-list")
          .addEventListener("change", refresh);
        const busca = document.getElementById("sw-aluno-busca");
        busca.addEventListener("input", () => {
          const q = busca.value.trim().toLowerCase();
          document
            .querySelectorAll("#sw-aluno-list .ait-row")
            .forEach((row) => {
              row.style.display =
                !q || row.dataset.busca.includes(q) ? "" : "none";
            });
        });
        refresh();
      },
      preConfirm: () => {
        const nome = document.getElementById("sw-nome").value.trim();
        if (!nome) {
          Swal.showValidationMessage("Nome é obrigatório");
          return false;
        }
        const coordenacaoIds = Array.from(
          document.querySelectorAll(".ait-cb-coord:checked"),
        ).map((c) => c.value);
        const alunoIds = Array.from(
          document.querySelectorAll(".ait-cb-aluno:checked"),
        ).map((c) => c.value);
        if (coordenacaoIds.length === 0) {
          Swal.showValidationMessage("Selecione pelo menos uma coordenação");
          return false;
        }
        if (alunoIds.length === 0) {
          Swal.showValidationMessage("Selecione pelo menos um aluno");
          return false;
        }
        return {
          nome,
          complemento: document.getElementById("sw-comp").value.trim() || null,
          tipo: document.getElementById("sw-tipo").value,
          coordenacaoIds,
          alunoIds,
        };
      },
    });
    if (!isConfirmed) return;
    try {
      await adminTurmasApi.criar(instId, value);
      Swal.fire("OK", "Turma criada", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao criar", "error");
    }
  };

  const onEditar = async (t) => {
    // Coordenações são geridas na seção expandida (não no modal de edição).
    const { value, isConfirmed } = await Swal.fire({
      title: "Editar turma",
      html: `
        <input id="sw-nome" class="swal2-input" value="${t.nome}" />
        <input id="sw-comp" class="swal2-input" value="${t.complemento || ""}" />
        <select id="sw-tipo" class="swal2-input">
          <option value="primario" ${t.tipo === "primario" ? "selected" : ""}>Primária</option>
          <option value="secundario" ${t.tipo === "secundario" ? "selected" : ""}>Secundária</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: () => ({
        nome: document.getElementById("sw-nome").value.trim(),
        complemento: document.getElementById("sw-comp").value.trim() || null,
        tipo: document.getElementById("sw-tipo").value,
      }),
    });
    if (!isConfirmed) return;
    try {
      await adminTurmasApi.editar(instId, t.id, value);
      Swal.fire("OK", "Turma atualizada", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao salvar", "error");
    }
  };

  const onExcluir = async (t) => {
    const step1 = await Swal.fire({
      title: `Excluir ${t.nome}?`,
      text: "Essa ação é irreversível. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step1.isConfirmed) return;

    const step2 = await Swal.fire({
      title: "Confirmação final",
      html: `<p>Todos os dados da turma <strong>${t.nome}</strong> e seus vínculos serão excluídos permanentemente.</p><p><strong>Essa ação não pode ser desfeita.</strong></p>`,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Excluir definitivamente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step2.isConfirmed) return;

    try {
      await adminTurmasApi.excluir(instId, t.id);
      Swal.fire("OK", "Turma excluída", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao excluir", "error");
    }
  };

  const onAdicionarAluno = async (turmaId) => {
    try {
      const res = await adminApi.listarUsuarios({
        instituicaoId: instId,
        tipo: TipoUsuario.Aluno,
        pageSize: 200,
      });
      const usuarios = res.items || [];
      if (usuarios.length === 0) {
        Swal.fire("Aviso", "Nenhum aluno disponível nesta instituição", "info");
        return;
      }
      const opts = usuarios
        .map((u) => `<option value="${u.id}">${u.nome} (${u.email})</option>`)
        .join("");
      const { value, isConfirmed } = await Swal.fire({
        title: "Vincular aluno",
        html: `<select id="sw-aluno" class="swal2-input"><option value="">Selecione um aluno...</option>${opts}</select>`,
        showCancelButton: true,
        confirmButtonText: "Vincular",
        cancelButtonText: "Cancelar",
        preConfirm: () => {
          const val = document.getElementById("sw-aluno").value;
          if (!val) {
            Swal.showValidationMessage("Selecione um aluno");
            return false;
          }
          return val;
        },
      });
      if (!isConfirmed) return;
      await adminTurmasApi.vincularAluno(instId, turmaId, value);
      Swal.fire("OK", "Aluno vinculado", "success");
      await carregarDetalhes(turmaId);
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao vincular", "error");
    }
  };

  // Vincular coordenação à turma. Os coordenadores dela viram responsáveis
  // automaticamente (sync backend). Painel admin não adiciona responsável avulso.
  const onVincularCoordenacao = async (turmaId, jaVinculadasIds) => {
    let coords = [];
    try {
      coords = (await adminCoordenacoesApi.listar(instId)) || [];
    } catch {
      coords = [];
    }
    const disponiveis = coords.filter(
      (c) => !jaVinculadasIds.some((id) => String(id) === String(c.id)),
    );
    if (disponiveis.length === 0) {
      Swal.fire("Aviso", "Todas as coordenações já estão vinculadas.", "info");
      return;
    }
    const opts = disponiveis
      .map((c) => `<option value="${c.id}">${c.nome}</option>`)
      .join("");
    const { value, isConfirmed } = await Swal.fire({
      title: "Vincular coordenação",
      html: `<select id="sw-coord" class="swal2-input"><option value="">Selecione...</option>${opts}</select>`,
      showCancelButton: true,
      confirmButtonText: "Vincular",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const v = document.getElementById("sw-coord").value;
        if (!v) {
          Swal.showValidationMessage("Selecione uma coordenação");
          return false;
        }
        return v;
      },
    });
    if (!isConfirmed) return;
    try {
      await adminTurmasApi.vincularCoordenacao(instId, turmaId, value);
      Swal.fire("OK", "Coordenação vinculada", "success");
      await carregarDetalhes(turmaId);
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao vincular", "error");
    }
  };

  const onDesvincularCoordenacao = async (turmaId, coordenacaoId) => {
    const ok = await Swal.fire({
      title: "Remover coordenação?",
      text: "Os coordenadores dela deixam de ser responsáveis da turma (salvo se também estiverem em outra coordenação vinculada).",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await adminTurmasApi.desvincularCoordenacao(instId, turmaId, coordenacaoId);
      Swal.fire("OK", "Coordenação removida", "success");
      await carregarDetalhes(turmaId);
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao remover", "error");
    }
  };

  const onDesvincularAluno = async (turmaId, alunoId) => {
    const ok = await Swal.fire({
      title: "Remover aluno?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await adminTurmasApi.desvincularAluno(instId, turmaId, alunoId);
      Swal.fire("OK", "Aluno removido", "success");
      await carregarDetalhes(turmaId);
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao remover", "error");
    }
  };

  const renderPermissoes = (resp) => {
    const perms = [];
    if (resp.criarEditarEventos) perms.push("Criar/Editar Eventos");
    if (resp.editarListaAlunos) perms.push("Editar Lista");
    if (resp.gerenciarResponsaveis) perms.push("Gerenciar Resp.");
    return perms.length > 0 ? perms.join(" • ") : "Sem permissões";
  };

  return (
    <div className="admin-inst-turmas">
      <header className="topo">
        <h2>Turmas</h2>
        <button className="primary" onClick={onCriar}>
          Nova turma
        </button>
      </header>
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <ul className="lista">
          {turmas.length === 0 && (
            <li className="vazio">Nenhuma turma cadastrada.</li>
          )}
          {turmas.map((t) => (
            <li key={t.id} className="turma-item">
              <div className="turma-header">
                <div className="turma-info">
                  <strong>{t.nome}</strong>
                  {t.complemento && (
                    <span className="comp">— {t.complemento}</span>
                  )}
                  <span className={`tag ${String(t.tipo || "").toLowerCase()}`}>
                    {t.tipo === "secundario" ? "Secundária" : "Primária"}
                  </span>
                </div>
                <div className="turma-actions">
                  <button
                    className="toggle-btn"
                    onClick={() => {
                      if (turmaAberta === t.id) {
                        setTurmaAberta(null);
                      } else {
                        setTurmaAberta(t.id);
                        if (!detalhes[t.id]) {
                          carregarDetalhes(t.id);
                        }
                      }
                    }}
                  >
                    {turmaAberta === t.id ? "Fechar" : "Expandir"}
                  </button>
                  <button onClick={() => onEditar(t)}>Editar</button>
                  <button className="danger" onClick={() => onExcluir(t)}>
                    Excluir
                  </button>
                </div>
              </div>

              {turmaAberta === t.id && (
                <div className="turma-details">
                  <div className="sub-section">
                    <h3>Coordenações vinculadas</h3>
                    <button
                      className="add-btn"
                      onClick={() =>
                        onVincularCoordenacao(
                          t.id,
                          (detalhes[t.id]?.coordenacoes || []).map((c) => c.id),
                        )
                      }
                    >
                      + Vincular coordenação
                    </button>
                    {detalhes[t.id]?.coordenacoes &&
                    detalhes[t.id].coordenacoes.length > 0 ? (
                      <ul className="vinculos-list">
                        {detalhes[t.id].coordenacoes.map((c) => (
                          <li key={c.id} className="vinculo-item">
                            <div className="vinculo-info">
                              <strong>{c.nome}</strong>
                            </div>
                            <button
                              className="remover-btn"
                              onClick={() => onDesvincularCoordenacao(t.id, c.id)}
                              title="Remover coordenação"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="vazio-subitem">
                        Nenhuma coordenação vinculada.
                      </p>
                    )}
                  </div>

                  <div className="sub-section">
                    <h3>Alunos vinculados</h3>
                    <button
                      className="add-btn"
                      onClick={() => onAdicionarAluno(t.id)}
                    >
                      + Adicionar aluno
                    </button>
                    {detalhes[t.id]?.alunos &&
                    detalhes[t.id].alunos.length > 0 ? (
                      <ul className="vinculos-list">
                        {detalhes[t.id].alunos.map((aluno) => (
                          <li key={aluno.usuarioId} className="vinculo-item">
                            <div className="vinculo-info">
                              <strong>{aluno.nome}</strong>
                              <span className="email">{aluno.email}</span>
                            </div>
                            <button
                              className="remover-btn"
                              onClick={() =>
                                onDesvincularAluno(t.id, aluno.usuarioId)
                              }
                              title="Remover aluno"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="vazio-subitem">Nenhum aluno vinculado.</p>
                    )}
                  </div>

                  <div className="sub-section">
                    <h3>Responsáveis (automático das coordenações)</h3>
                    <p className="vazio-subitem" style={{ marginTop: 0 }}>
                      Definidos pelas coordenações vinculadas. Para alterar, gerencie
                      as coordenações acima. Cargo/contato editáveis pela interface.
                    </p>
                    {detalhes[t.id]?.responsaveis &&
                    detalhes[t.id].responsaveis.length > 0 ? (
                      <ul className="vinculos-list">
                        {detalhes[t.id].responsaveis.map((resp) => (
                          <li
                            key={resp.vinculoId}
                            className="vinculo-item resp-item"
                          >
                            <div className="vinculo-info">
                              <strong>{resp.nome}</strong>
                              <span className="email">{resp.email}</span>
                              {resp.cargo && (
                                <span className="cargo">{resp.cargo}</span>
                              )}
                              {resp.contato && (
                                <span className="contato">
                                  {formatContatoDisplay(resp.contato)}
                                </span>
                              )}
                              <span className="permissoes">
                                {renderPermissoes(resp)}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="vazio-subitem">
                        Nenhum responsável (vincule uma coordenação).
                      </p>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
